import { useTranslation } from "react-i18next"
import { TemplateDefaultAdminConsole } from "../../../components/TemplateDefaultAdminConsole"
import { Apartment, Check, Delete, Person, Search, Workspaces } from "@mui/icons-material";
import {
    AlertColor, Box, Checkbox, Divider, IconButton,
    InputAdornment, List, ListItem, ListItemButton, ListItemIcon,
    TextField, Typography
} from "@mui/material";
import { KeydriveTextfield } from "../../../../../components/KeydriveComponents/Textfield/KeydriveTextfield";
import { useEffect, useState } from "react";
import { AddUserBatchVO, IUserInfo, Organization, UserAddBatchVO } from "../../../../../model/OrganizationModel";
import { WorkSpace } from "../../../../../model/WorkSpaceModel";
import { useNavigate, useParams } from "react-router-dom";
import { AddUserBatch, GetOrganizationTrim } from "../../../../../services/OrganizationServices";
import { GetOrgWorkSpaceTrim } from "../../../../../services/WorkSpaceServices";
import KeydriveSnackbar from "../../../../../components/KeydriveComponents/Snackbar/KeydriveSnackbar";
import LoaderComp from "../../../../../components/Loader/LoaderComp";
import { KeyDriveDialog } from "../../../../../components/KeydriveComponents/Dialog/KeyDriveDialog";
import { UserCheckCircleIcon } from "../../../../../assets/icons/UserCheck";
import { UserOffCircleIcon } from "../../../../../assets/icons/UserOffCircleIcon";
import { UsersDownloadCircleIcon } from "../../../../../assets/icons/UserDownloadCircleIcon";
import { KeyDriveBodyMax } from "../../../../../components/KeydriveComponents/Fonts/Body/KeyDriveBodyMax";
import { EmptyContainer } from "../../../../../components/KeydriveComponents/EmptyContainer/EmptyContainer";
import { NoUsersIcon } from "../../../../../assets/icons/NoUsersIcon";
import { KeyDriveBodyMin } from "../../../../../components/KeydriveComponents/Fonts/Body/KeyDriveBodyMin";
import { UserCheckIcon } from "../../../../../assets/icons/UserCheckIcon";
import { KeyDrivePrimaryButton } from "../../../../../components/KeydriveComponents/Buttons/KeyDrivePrimaryButton";
import { validEmail } from "../../../../WizardFree/validators/stepTwoValidation";
import KeydriveAlert from "../../../../../components/KeydriveComponents/Alert/KeyDriveAlert";

interface OrgRuntimeData {
    workspaces: WorkSpace[];
    selectedWsIds: string[];
}

const emptyForm = (): AddUserBatchVO => ({
    email: "",
    name: "",
    info: [],
});

enum UserBatchModalState {
    DEFAULT = 0,
    PARTIAL_SUCCESS,
    ERROR
}


const ColumnSearch = ({ value, onChange }: { value: string; onChange: (v: string) => void }) => {
    const { t } = useTranslation();

    return (
         <TextField
            size="small"
            fullWidth
            placeholder={t("RecicleBin.search")}
            value={value}
            onChange={e => onChange(e.target.value)}
            InputProps={{
                startAdornment: (
                    <InputAdornment position="start">
                        <Search sx={{ fontSize: 16, color: "#9E9E9E" }} />
                    </InputAdornment>
                ),
            }}
            sx={{
                mb: 1,
                "& .MuiOutlinedInput-root": {
                    borderRadius: "4px",
                    fontSize: "13px",
                    "& fieldset": { borderColor: "#E9EFF4" },
                },
            }}
        />
    );
}

const UserAvatar = ({ name, active }: { name: string; active?: boolean }) => {
    const initials = name.split(" ").slice(0, 2).map(w => w[0]?.toUpperCase() ?? "").join("");
    return (
        <Box sx={{
            width: 28, height: 28, borderRadius: "50%",
            bgcolor: active ? "#BDD0DD" : "#B0BEC5",
            color: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "11px", fontWeight: 600, flexShrink: 0,
            transition: "background 0.2s",
        }}>
            {initials}
        </Box>
    );
};

const PanelColumn = ({ title, children, visible = true }: {
    title: string;
    children: React.ReactNode;
    visible?: boolean;
}) => {
    if (!visible) return null;
    return (
        <Box sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            borderRight: "1px solid #E0E0E0",
            px: 2,
            pt: 1,
            overflow: "hidden",
            "&:last-child": { borderRight: "none" },
        }}>
            <Box mb={1.5} pb={1} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                <KeyDriveBodyMax color="#2d2d2d" needBold needEllipses={false} text={title} />
            </Box>
            {children}
        </Box>
    );
};

const buildInfoFromRuntime = (
    selectedOrgIds: string[],
    orgRuntimeMap: Record<string, OrgRuntimeData>
): IUserInfo[] => {
    const rows: IUserInfo[] = [];
    for (const orgId of selectedOrgIds) {
        const runtime = orgRuntimeMap[orgId];
        if (!runtime) continue;
        if (runtime.selectedWsIds.length > 0) {
            for (const wsId of runtime.selectedWsIds) {
                rows.push({
                    organization: orgId,
                    workspace: wsId,
                    workspaces: []
                });
            }
        } else {
            rows.push({
                organization: orgId,
                workspace: "",
                workspaces: [],
            });
        }
    }
    return rows;
};


export const AddUserBatchPanel = () => {
    const { t, i18n } = useTranslation();
    const { nameOrg } = useParams();
    const lang = i18n.language;
    const navigate = useNavigate();

    const [form, setForm] = useState<AddUserBatchVO>(emptyForm());
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [loadingOrganizations, setLoadingOrganizations] = useState<boolean>(false);
    const [usersToAdd, setUsersToAdd] = useState<AddUserBatchVO[]>([]);
    const [message, setMessage] = useState<string | undefined>();
    const [alertColor, setAlertColor] = useState<AlertColor>("success");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [modalState, setModalState] = useState<UserBatchModalState>(UserBatchModalState.DEFAULT);
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [userAlreadyInModalOpen, setUserAlreadyInModalOpen] = useState<boolean>(false);
    const [batchData, setBatchData] = useState<UserAddBatchVO>({ userErrorQuantity: 0, userSuccessQuantity: 0 });
    const [activeUserEmail, setActiveUserEmail] = useState<string | null>(null);
    const [userOrgRuntime, setUserOrgRuntime] = useState<
        Record<string, Record<string, OrgRuntimeData>>
    >({});
    const [activeSelectedOrgs, setActiveSelectedOrgs] = useState<string[]>([]);
    const [focusedOrg, setFocusedOrg] = useState<string | null>(null);
    const [userSearch, setUserSearch] = useState("");
    const [orgSearch, setOrgSearch] = useState("");
    const [wsSearch, setWsSearch] = useState("");
    const [formPrivilege, setFormPrivilege] = useState<number>(5);
    const [isSelectingAllOrgs, setIsSelectingAllOrgs] = useState<boolean>(false);
    const [isSelectingAllWs, setIsSelectingAllWs] = useState<boolean>(false);

    useEffect(() => {
        if (!nameOrg) return;
        setLoadingOrganizations(true);
        GetOrganizationTrim(0, 999, false)
        .then(data => {
            if (data) setOrganizations(data);
        })
        .finally(() => {
            setLoadingOrganizations(false);
        })
    }, []);

    useEffect(() => {

        if (!activeUserEmail) {
            resetForm();
            return;
        }

        let activeUser = usersToAdd?.filter(user => user.email == activeUserEmail)[0];
        setFormActiveUserInfo(activeUser);
    }, [activeUserEmail]);

    useEffect(() => {
        canAddWorkspacePrivilege();
    }, [activeUserEmail, focusedOrg, usersToAdd])

    useEffect(() => {
        if (organizations.length === 0) return;
        const allSelected = organizations.every(org =>
            activeSelectedOrgs.includes(org.internalName!)
        );
        setIsSelectingAllOrgs(allSelected);
    }, [activeSelectedOrgs, organizations]);

    useEffect(() => {
        if (!activeUserEmail || activeSelectedOrgs.length === 0) {
            setIsSelectingAllWs(false);
            return;
        }

        const userRuntime = userOrgRuntime[activeUserEmail];
        if (!userRuntime) {
            setIsSelectingAllWs(false);
            return;
        }

        let totalWs = 0;
        let totalSelected = 0;
        for (const orgId of activeSelectedOrgs) {
            const runtime = userRuntime[orgId];
            if (!runtime) continue;
            totalWs += runtime.workspaces.length;
            totalSelected += runtime.selectedWsIds.length;
        }

        if (totalWs === 0) {
            setIsSelectingAllWs(false);
            return;
        }

        setIsSelectingAllWs(totalSelected === totalWs);
    }, [userOrgRuntime, activeUserEmail, activeSelectedOrgs]);

    const setFormActiveUserInfo = (activeUser: AddUserBatchVO | undefined): void => {
        if (activeUser == undefined) return;
        
        setForm({
            email: activeUser?.email,
            info: activeUser?.info,
            name: activeUser?.name
        });
    }

    const resetForm = (): void => {
        setForm({ name: "", email: "", info: [] } as AddUserBatchVO);
    }

    const canAddWorkspacePrivilege = (): void => {
        let info = usersToAdd.filter(user => user.email == activeUserEmail!)[0]?.info
                                    .filter(inf => inf.organization == focusedOrg)[0];

        if (info == undefined) {
            return;
        }
    }

    const syncUserInfo = (
        email: string,
        selectedOrgs: string[],
        runtimeMap: Record<string, OrgRuntimeData>
    ) => {
        const info = buildInfoFromRuntime(selectedOrgs, runtimeMap);
        setUsersToAdd(prev =>
            prev.map(u => u.email === email ? { ...u, info } : u)
        );
    };

    const activateUser = (email: string) => {
        if (activeUserEmail === email) {
            setActiveUserEmail(null);
            setActiveSelectedOrgs([]);
            setFocusedOrg(null);
            return;
        }
        setActiveUserEmail(email);
        setFocusedOrg(null);
        setWsSearch("");

        const runtime = userOrgRuntime[email] ?? {};
        const orgs = Object.keys(runtime);
        setActiveSelectedOrgs(orgs);
    };

    const selectAllOrgs = async () => {
        if (!activeUserEmail) return;
        if (!organizations || organizations.length === 0) return;

        const orgNames = organizations.map(o => o.internalName!);

        setActiveSelectedOrgs(orgNames);
        setFocusedOrg(null);

        let accumulatedRuntime: Record<string, OrgRuntimeData> = {
            ...(userOrgRuntime[activeUserEmail] ?? {})
        };

        for (const org of organizations) {
            const orgId = org.internalName!;
            const existing = accumulatedRuntime[orgId];

            if (!existing) {
                const data = await GetOrgWorkSpaceTrim(orgId, 0, 999, false);
                accumulatedRuntime[orgId] = {
                    workspaces: data ?? [],
                    selectedWsIds: [],
                };
            }
        }

        setUserOrgRuntime(prev => ({ ...prev, [activeUserEmail]: accumulatedRuntime }));
        syncUserInfo(activeUserEmail, orgNames, accumulatedRuntime);
        setIsSelectingAllOrgs(true);
    };

    const deselectAllOrgs = () => {
        if (!activeUserEmail) return;
        setActiveSelectedOrgs([]);
        setFocusedOrg(null);
        setUserOrgRuntime(prev => ({ ...prev, [activeUserEmail]: {} }));
        syncUserInfo(activeUserEmail, [], {});
        setIsSelectingAllOrgs(false);
        setIsSelectingAllWs(false);
    };

    const selectAllWorkspaces = () => {
        if (!activeUserEmail || activeSelectedOrgs.length === 0) return;

        const userRuntime = userOrgRuntime[activeUserEmail] ?? {};

        let totalWs = 0;
        let totalSelected = 0;
        for (const orgId of activeSelectedOrgs) {
            const runtime = userRuntime[orgId];
            if (!runtime) continue;
            totalWs += runtime.workspaces.length;
            totalSelected += runtime.selectedWsIds.length;
        }
        const allSelected = totalWs > 0 && totalSelected === totalWs;

        const updatedUserRuntime: Record<string, OrgRuntimeData> = { ...userRuntime };
        for (const orgId of activeSelectedOrgs) {
            const runtime = updatedUserRuntime[orgId];
            if (!runtime) continue;
            updatedUserRuntime[orgId] = {
                ...runtime,
                selectedWsIds: allSelected ? [] : runtime.workspaces.map(ws => ws.id!),
            };
        }

        setUserOrgRuntime(prev => ({ ...prev, [activeUserEmail]: updatedUserRuntime }));
        syncUserInfo(activeUserEmail, activeSelectedOrgs, updatedUserRuntime);
        setIsSelectingAllWs(!allSelected);
    };

    const handleOrgToggle = async (orgInternalName: string) => {
        if (!activeUserEmail) return;

        const isAlreadySelected = activeSelectedOrgs.includes(orgInternalName);

        if (isAlreadySelected) {
            const newSelected = activeSelectedOrgs.filter(o => o !== orgInternalName);
            setActiveSelectedOrgs(newSelected);

            const newRuntime = { ...(userOrgRuntime[activeUserEmail] ?? {}) };
            delete newRuntime[orgInternalName];
            setUserOrgRuntime(prev => ({ ...prev, [activeUserEmail]: newRuntime }));

            if (focusedOrg === orgInternalName) setFocusedOrg(null);
            syncUserInfo(activeUserEmail, newSelected, newRuntime);
        } else {
            const existingRuntime = userOrgRuntime[activeUserEmail]?.[orgInternalName];
            let workspaces: WorkSpace[] = existingRuntime?.workspaces ?? [];

            if (!existingRuntime) {
                const data = await GetOrgWorkSpaceTrim(orgInternalName, 0, 999, false);
                workspaces = data ?? [];
            }

            const newSelected = [...activeSelectedOrgs, orgInternalName];
            setActiveSelectedOrgs(newSelected);

            const newRuntime: OrgRuntimeData = existingRuntime ?? {
                workspaces,
                selectedWsIds: [],
                privilege: formPrivilege,
            };

            const updatedUserRuntime = {
                ...(userOrgRuntime[activeUserEmail] ?? {}),
                [orgInternalName]: { ...newRuntime, workspaces },
            };
            setUserOrgRuntime(prev => ({ ...prev, [activeUserEmail]: updatedUserRuntime }));

            setFocusedOrg(orgInternalName);
            syncUserInfo(activeUserEmail, newSelected, updatedUserRuntime);
        }
    };

    const handleWsToggle = (wsId: string, wsOrgId: string) => {
        if (!activeUserEmail) return;

        const currentRuntime = userOrgRuntime[activeUserEmail]?.[wsOrgId];
        if (!currentRuntime) return;

        const newWsIds = currentRuntime.selectedWsIds.includes(wsId)
            ? currentRuntime.selectedWsIds.filter(id => id !== wsId)
            : [...currentRuntime.selectedWsIds, wsId];

        const updatedUserRuntime = {
            ...(userOrgRuntime[activeUserEmail] ?? {}),
            [wsOrgId]: { ...currentRuntime, selectedWsIds: newWsIds },
        };
        setUserOrgRuntime(prev => ({ ...prev, [activeUserEmail]: updatedUserRuntime }));
        syncUserInfo(activeUserEmail, activeSelectedOrgs, updatedUserRuntime);
    };

    const confirmUser = () => {
        if (!form.email || !form.name) {
            setMessage(t("AdminConsole.FillRequiredFields"));
            setAlertColor("error");
            return;
        }
        if (usersToAdd.some(u => u.email === form.email)) {
            setUserAlreadyInModalOpen(true);
            return;
        }

        if (form.name.length > 50) {
            setMessage(t("AdminConsole.AddBatchUser.NameAbove50Chars"));
            setAlertColor("error");
            return;
        }

        if (!validEmail(form.email)) {
            setMessage(t("AdminConsole.AddBatchUser.EmailInvalid"));
            setAlertColor("error");
            return;
        }

        const newUser: AddUserBatchVO = { ...form, info: [] };
        setUsersToAdd(prev => [...prev, newUser]);
        setUserOrgRuntime(prev => ({ ...prev, [form.email]: {} }));

        setActiveUserEmail(form.email);
        setActiveSelectedOrgs([]);
        setFocusedOrg(null);

        setForm(emptyForm());
        setFormPrivilege(5);
    };

    const removeUser = (email: string) => {
        setUsersToAdd(prev => prev.filter(u => u.email !== email));
        setUserOrgRuntime(prev => {
            const next = { ...prev };
            delete next[email];
            return next;
        });
        if (activeUserEmail === email) {
            setActiveUserEmail(null);
            setActiveSelectedOrgs([]);
            setFocusedOrg(null);
        }
    };

    const handleSubmit = () => {
        setIsLoading(true);
        AddUserBatch(usersToAdd)
            .then(data => {
                setBatchData(data);
                if (data.userErrorQuantity > 0) {
                    setModalState(UserBatchModalState.PARTIAL_SUCCESS);
                }
                setModalOpen(true);
            })
            .catch(() => {
                setModalState(UserBatchModalState.ERROR);
                setModalOpen(true);
            })
            .finally(() => setIsLoading(false));
    };

    const canAdd = !!form.name && !!form.email;
    const showOrgColumn = true;

    const mergedWorkspaces: WorkSpace[] = activeUserEmail
        ? activeSelectedOrgs.flatMap(orgId =>
            userOrgRuntime[activeUserEmail]?.[orgId]?.workspaces ?? []
          )
        : [];

    const showWsColumn = showOrgColumn && mergedWorkspaces.length > 0;

    const filteredUsers = usersToAdd.filter(u =>
        u.name.toLowerCase().includes(userSearch.toLowerCase()) ||
        u.email.toLowerCase().includes(userSearch.toLowerCase())
    );
    const filteredOrgs = organizations.filter(o =>
        o?.name!.toLowerCase().includes(orgSearch.toLowerCase())
    );
    const filteredWs = mergedWorkspaces.filter(w =>
        w?.name!.toLowerCase().includes(wsSearch.toLowerCase())
    );

    if (isLoading) {
        return (
            <TemplateDefaultAdminConsole
                title={t("AdminConsole.NewUserBatch")}
                subtitle={t("AdminConsole.AddUserSubtitle")}
                iconTitle={<Person />}
            >
                <Box display="flex" justifyContent="center">
                    <LoaderComp big />
                </Box>
            </TemplateDefaultAdminConsole>
        );
    }

    return (
        <TemplateDefaultAdminConsole
            title={t("AdminConsole.NewUserBatch")}
            subtitle={t("AdminConsole.AddUserSubtitle")}
            iconTitle={<Person />}
            primaryButtonFunction={handleSubmit}
            primaryButtonText={t("Privileges.Confirm")}
            secondaryButtonText={t("Privileges.Cancel")}
            secondaryButtonFunction={() => navigate(-1)}
            needButtons
            disabledButtons={usersToAdd.length <= 0}
        >
            <Box sx={{
                display: "flex",
                border: "1px solid #E0E0E0",
                borderRadius: "8px",
                overflow: "hidden",
                height: "100%",
                bgcolor: "#fff"
            }}>

                <PanelColumn title={activeUserEmail ? t("AdminConsole.EditUserInfo") : t("AdminConsole.NewUser")}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, flex: 1 }}>
                        <Box>
                            <Typography sx={{ fontSize: "12px", color: "#424242", mb: 0.5 }}>
                                {t("AdminConsole.Username")} *
                            </Typography>
                            <KeydriveTextfield
                                needFullWidth
                                value={form.name}
                                onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                                label=""
                                placeholder={t("DetailPhLocation.TypeHereAdditionalInformation")}
                            />
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: "12px", color: "#424242", mb: 0.5 }}>
                                E-mail *
                            </Typography>
                            <KeydriveTextfield
                                needFullWidth
                                value={form.email}
                                onChange={e => setForm(prev => ({ ...prev, email: e.target.value }))}
                                label=""
                                placeholder={t("DetailPhLocation.TypeHereAdditionalInformation")}
                            />
                        </Box>
                        <Box>
                            <KeydriveAlert 
                                title={t("AdminConsole.AddBatchUser.UserAddedBatch")}
                                subtitle={t("AdminConsole.AddBatchUser.UserAddedBatchContent")}
                                variant="warning"
                            />
                        </Box>
                    </Box>

                    <Box sx={{ mt: "auto", pt: 2, mb: "20px" }}>
                        <KeyDrivePrimaryButton 
                            TextComponent={KeyDriveBodyMin}
                            TextComponentProps={{
                                color: '#FFFFFF',
                                needBold: false,
                                needEllipses: false,
                                text: t("Privileges.Add")
                            }}
                            startIcon={<Check />}
                            onClick={confirmUser}
                            disabled={!canAdd}
                            needFullWidth
                        />
                    </Box>
                </PanelColumn>

                <PanelColumn title={t("AdminConsole.AddBatchUser.UsersAdded")} visible={true}>
                    <ColumnSearch value={userSearch} onChange={setUserSearch} />
                    <List dense disablePadding sx={{ overflow: "auto", flex: 1 }}>
                        {usersToAdd.length <= 0 ? (
                            <EmptyContainer
                                content={t('AdminConsole.AddBatchUser.NoUsersAddedContent')}
                                title={t('AdminConsole.AddBatchUser.NoUsersAddedTitle')}
                                icon={<NoUsersIcon />}
                            />
                        ) : filteredUsers.map(user => {
                            const isActive = activeUserEmail === user.email;
                            const orgCount = Object.keys(userOrgRuntime[user.email] ?? {}).length;
                            return (
                                <ListItem
                                    key={user.email}
                                    disablePadding
                                    secondaryAction={
                                        <IconButton
                                            size="small"
                                            edge="end"
                                            onClick={e => { e.stopPropagation(); removeUser(user.email); }}
                                            sx={{ color: "#9E9E9E" }}
                                        >
                                            <Delete sx={{ fontSize: 18, color: isActive ? "#22638D" : "#9E9E9E" }} />
                                        </IconButton>
                                    }
                                    sx={{
                                        mb: 0.75,
                                        border: isActive ? "1px solid #22638D" : "1px solid #EAEAEA",
                                        borderRadius: "6px",
                                        bgcolor: isActive ? "#F0F7FC" : "transparent",
                                        transition: "all 0.15s",
                                    }}
                                >
                                    <ListItemButton
                                        onClick={() => activateUser(user.email)}
                                        sx={{
                                            borderRadius: "6px",
                                            py: "6px",
                                            px: 1,
                                            "&:hover": { bgcolor: "transparent" },
                                        }}
                                        disableRipple
                                    >
                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                            <UserAvatar name={user.name} active={isActive} />
                                        </ListItemIcon>
                                        <Box>
                                            <Typography sx={{ fontSize: "13px", color: isActive ? "#22638D" : "#424242", fontWeight: isActive ? 600 : 500, lineHeight: 1.3 }}>
                                                {user.name}
                                            </Typography>
                                            {orgCount > 0 && (
                                                <Typography sx={{ fontSize: "11px", color: "#9E9E9E", lineHeight: 1.2 }}>
                                                    {orgCount} org{orgCount > 1 ? "s" : ""}
                                                </Typography>
                                            )}
                                        </Box>
                                    </ListItemButton>
                                </ListItem>
                            );
                        })}
                    </List>
                </PanelColumn>

                <PanelColumn title={t("Wizard.organization")} visible={showOrgColumn}>
                    <Box display="flex" mb={1} gap="12px">
                        <Box
                            border="1px solid #E9EFF4"
                            height="36px"
                            width="36px"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            title={isSelectingAllOrgs ? "Desmarcar todas as organizações" : "Selecionar todas as organizações"}
                        >
                            <Checkbox
                                checked={isSelectingAllOrgs}
                                onClick={e => {
                                    if (!activeUserEmail) return;
                                    e.stopPropagation();
                                    if (isSelectingAllOrgs) {
                                        deselectAllOrgs();
                                    } else {
                                        selectAllOrgs();
                                    }
                                }}
                                sx={{
                                    color: "#22638D",
                                    "&.Mui-checked": { color: "#22638D" },
                                }}
                                disabled={activeUserEmail == null}
                            />
                        </Box>
                        <ColumnSearch value={orgSearch} onChange={setOrgSearch} />
                    </Box>
                    <Box sx={{ overflow: "auto", flex: 1 }}>
                        {loadingOrganizations ? (
                            <Box display="flex" justifyContent="center"><LoaderComp /></Box>
                        ) : filteredOrgs.map(org => {
                            const isChecked = activeSelectedOrgs.includes(org.internalName!);
                            const isFocused = focusedOrg === org.internalName;
                            const wsCount = activeUserEmail
                                ? (userOrgRuntime[activeUserEmail]?.[org.internalName!]?.selectedWsIds.length ?? 0)
                                : 0;

                            return (
                                <Box
                                    key={org.internalName}
                                    onClick={() => {
                                        if (isChecked) {
                                            setFocusedOrg(isFocused ? null : org.internalName!);
                                        } else {
                                            if (activeUserEmail == null) {
                                                setMessage(t("AdminConsole.AddBatchUser.SelectUserToSelectOrg"));
                                                setAlertColor("warning");
                                                handleOrgToggle(org.internalName!);
                                            }
                                        }
                                    }}
                                    sx={{
                                        mb: "8px",
                                        border: isFocused
                                            ? "1px solid #22638D"
                                            : isChecked
                                                ? "1px solid #90BDD9"
                                                : "1px solid #E9EFF4",
                                        borderRadius: "4px",
                                        cursor: "pointer",
                                        bgcolor: isFocused ? "#F0F7FC" : "transparent",
                                        "&:hover": { bgcolor: isFocused ? "#E8F1F8" : "#F5F5F5" },
                                        transition: "all 0.15s",
                                    }}
                                >
                                    <Box sx={{ height: "36px", display: "flex", alignItems: "center" }}>
                                        <Checkbox
                                            checked={isChecked}
                                            onClick={e => {
                                                e.stopPropagation();
                                                handleOrgToggle(org.internalName!);
                                            }}
                                            sx={{
                                                color: "#22638D",
                                                "&.Mui-checked": { color: "#22638D" },
                                            }}
                                            disabled={activeUserEmail == null}
                                        />
                                        <Divider orientation="vertical" flexItem sx={{ bgcolor: "#E9EFF4", my: "6px" }} />
                                        <Box sx={{ mx: 0.75, display: "flex", alignItems: "center", color: "#22638D" }}>
                                            <Apartment fontSize="small" />
                                        </Box>
                                        <Box sx={{ flex: 1, overflow: "hidden" }}>
                                            <KeyDriveBodyMin 
                                                color="#22638D"
                                                text={org.name!}
                                                needBold={false}
                                                needEllipses={true}
                                            />
                                            {isChecked && wsCount > 0 && (
                                                <Typography sx={{ fontSize: "10px", color: "#9E9E9E", lineHeight: 1 }}>
                                                    {wsCount} workspace{wsCount > 1 ? "s" : ""}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                </PanelColumn>

                <PanelColumn title="Workspaces" visible={showWsColumn}>
                    <Box display="flex" mb={1} gap="12px">
                        <Box
                            border="1px solid #E9EFF4"
                            height="36px"
                            width="36px"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            title={isSelectingAllWs ? "Desmarcar todos os workspaces" : "Selecionar todos os workspaces"}
                        >
                            <Checkbox
                                checked={isSelectingAllWs}
                                onClick={e => {
                                    e.stopPropagation();
                                    selectAllWorkspaces();
                                }}
                                sx={{
                                    color: "#22638D",
                                    "&.Mui-checked": { color: "#22638D" },
                                }}
                                disabled={!activeUserEmail || activeSelectedOrgs.length === 0}
                            />
                        </Box>
                        <ColumnSearch value={wsSearch} onChange={setWsSearch} />
                    </Box>
                    <Box sx={{ overflow: "auto", flex: 1 }}>
                        {filteredWs.length === 0 && (
                            <Typography sx={{ fontSize: "12px", color: "#BDBDBD", textAlign: "center", mt: 3 }}>
                                Nenhum workspace disponível
                            </Typography>
                        )}
                        {filteredWs.map(ws => {
                            const wsOrgId = activeSelectedOrgs.find(orgId =>
                                userOrgRuntime[activeUserEmail!]?.[orgId]?.workspaces.some(w => w.id === ws.id)
                            ) ?? "";
                            const isChecked = wsOrgId && activeUserEmail
                                ? (userOrgRuntime[activeUserEmail]?.[wsOrgId]?.selectedWsIds.includes(ws.id!) ?? false)
                                : false;
                            return (
                                <Box
                                    key={ws.id}
                                    onClick={() => handleWsToggle(ws.id!, wsOrgId)}
                                    sx={{
                                        mb: "8px",
                                        border: isChecked ? "1px solid #90BDD9" : "1px solid #E9EFF4",
                                        borderRadius: "4px",
                                        cursor: "pointer",
                                        bgcolor: isChecked ? "#F0F7FC" : "transparent",
                                        "&:hover": { bgcolor: isChecked ? "#E8F1F8" : "#F5F5F5" },
                                        transition: "all 0.15s",
                                    }}
                                >
                                    <Box sx={{ height: "36px", display: "flex", alignItems: "center" }}>
                                        <Box sx={{ mx: 0.75, color: isChecked ? "#22638D" : "#757575", display: "flex", alignItems: "center" }}>
                                            <Workspaces fontSize="small" sx={{ color: "#22638D" }} />
                                        </Box>
                                        <KeyDriveBodyMin 
                                            text={ws.name!}
                                            color="#22638D"
                                            needBold={false}
                                            needEllipses={true}
                                        />
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                </PanelColumn>
            </Box>

            <KeydriveSnackbar
                message={message}
                open={message !== undefined}
                onClose={() => setMessage(undefined)}
                variant={alertColor}
            />

            <KeyDriveDialog
                key={String(modalOpen)}
                keyDriveDialogButtons={{
                    rightButtonText: t("AdminConsole.Confirm"),
                    rightButtonMethod: () => {
                        navigate(`/${nameOrg}/adminConsole/usersAndGroups`);
                        setModalOpen(false);
                        setModalState(UserBatchModalState.DEFAULT);
                    }
                }}
                keyDriveDialogContent={{
                    dialogContent: (
                        <Box sx={{ textAlign: "center" }}>
                            {modalState === UserBatchModalState.ERROR ? (
                                lang === "pt-BR"
                                    ? "Ocorreu um problema ao processar a solicitação. Tente novamente ou entre em contato com o suporte caso o problema persista."
                                    : lang === "es"
                                        ? "Ocurrió un problema al procesar la solicitud. Intenta de nuevo o contacta con el soporte si el problema persiste."
                                        : "There was a problem processing the request. Try again or contact support if the problem persists."
                            ) : modalState === UserBatchModalState.PARTIAL_SUCCESS ? (
                                <>
                                    <Box>{batchData?.userSuccessQuantity} {lang === "pt-BR" ? "usuários adicionados com sucesso" : lang === "es" ? "usuarios añadidos con éxito" : "users added successfully"}</Box>
                                    <Box>{batchData?.userErrorQuantity} {lang === "pt-BR" ? "usuários não puderam ser adicionados" : lang === "es" ? "usuarios no pudieron ser añadidos" : "users could not be added"}</Box>
                                </>
                            ) : (
                                <Box>{batchData?.userSuccessQuantity} {lang === "pt-BR" ? "usuários adicionados com sucesso" : lang === "es" ? "usuarios añadidos con éxito" : "users added successfully"}</Box>
                            )}
                        </Box>
                    ),
                    dialogIcon:
                        modalState === UserBatchModalState.ERROR ? <UserOffCircleIcon />
                        : modalState === UserBatchModalState.PARTIAL_SUCCESS ? <UsersDownloadCircleIcon />
                        : <UserCheckCircleIcon />,
                    dialogTitle:
                        modalState === UserBatchModalState.DEFAULT ? t("AdminConsole.UsersAddedSuccessFullyTitle")
                        : modalState === UserBatchModalState.ERROR ? t("AdminConsole.UsersCannotbeAddedTitle")
                        : t("AdminConsole.UsersAddedPartialTitle"),
                    handleTogglDialog: () => {},
                    openDialog: modalOpen,
                }}
                dialogTitleCenter
                width="600px"
                height="320px"
            />
            <KeyDriveDialog
                key={String(userAlreadyInModalOpen)}
                keyDriveDialogButtons={{
                    rightButtonText: t("AdminConsole.Confirm"),
                    rightButtonMethod: () => {
                        setUserAlreadyInModalOpen(false);
                    }
                }}
                keyDriveDialogContent={{
                    dialogContent: t("AdminConsole.AddBatchUser.UserAlreadyAddedContent"),
                    dialogIcon: <UserCheckIcon />,
                    dialogTitle: t("AdminConsole.AddBatchUser.UserAlreadyAdded"),
                    handleTogglDialog: () => {},
                    openDialog: userAlreadyInModalOpen,
                }}
                dialogTitleCenter
                width="600px"
                height="320px"
            />
        </TemplateDefaultAdminConsole>
    );
};